<main>
  <loading-overlay [isLoading]="isLoading()"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>

  <!-- Header -->
  <header>
    <div>
      <div class="flex gap-[2rem] items-center">
        <h1>
          @if (sqd()) {
            {{ 'SQD_Title' | translateWF: 'Second Quality Dispatch' }}: {{ sqd()!.number }}
          } @else {
            {{ 'SQD_New_Title' | translateWF: 'New Second Quality Dispatch' }}
          }
        </h1>
        @if (sqd()) {
          <p-tag [value]="getStatusLabel(sqd()!.status)" [severity]="getStatusSeverity(sqd()!.status)" />
        }
      </div>
    </div>
    <div class="buttons">
      @if (isCreateMode()) {
        <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="goBack()" />
        <p-button [label]="'SQD_Create' | translateWF: 'Create Dispatch'" icon="pi pi-check" (click)="createSqd()" [disabled]="initialForm.invalid" />
      }
      @if (sqd() && isDraft() && canEdit()) {
        <p-button [label]="'SQD_Download_PreInvoice' | translateWF: 'Download Pre-Invoice'" icon="pi pi-download" variant="outlined" (click)="downloadInvoice(true)" />
        <p-button [label]="'SQD_Complete' | translateWF: 'Complete'" icon="pi pi-check" severity="success" (click)="completeSqd()" />
        <p-button [label]="'SQD_Cancel' | translateWF: 'Cancel'" icon="pi pi-times" severity="danger" variant="outlined" (click)="cancelSqd()" />
      }
      @if (isCompleted()) {
        <p-button [label]="'SQD_Download_Invoice' | translateWF: 'Download Invoice'" icon="pi pi-download" variant="outlined" (click)="downloadInvoice(false)" />
      }
      @if (sqd()) {
        <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="goBack()" />
      }
    </div>
  </header>

  <!-- Content -->
  <div class="content">
    <!-- Invoice info (existing only) -->
    @if (sqd()?.invoiceNumber) {
      <p-card>
        <div class="grid-auto-3">
          <div class="form-box">
            <span class="text-gray-500 text-sm">{{ 'SQD_Invoice' | translateWF: 'Invoice Number' }}</span>
            <p class="font-semibold">{{ sqd()!.invoiceNumber }}</p>
          </div>
          <div class="form-box">
            <span class="text-gray-500 text-sm">{{ 'SQD_Invoice_Date' | translateWF: 'Invoice Date' }}</span>
            <p class="font-semibold">{{ sqd()!.invoiceGeneratedDate | date: 'mediumDate' }}</p>
          </div>
          <div class="form-box">
            <span class="text-gray-500 text-sm">{{ 'SQD_Exchange_Rate' | translateWF: 'Exchange Rate' }}</span>
            <p class="font-semibold">{{ sqd()!.exchangeRate ?? '-' }}</p>
          </div>
        </div>
      </p-card>
    }

    <!-- ===================== GENERAL INFORMATION ===================== -->
    <p-panel [header]="'SQD_General_Info' | translateWF: 'General Information'" [toggleable]="true" [collapsed]="false">
      <p-card data-variant="flat">
        @if (isCreateMode()) {
          <div class="grid-12">
            <div class="span-6">
              <div class="form-box w-full">
                <label>{{ 'SQD_Client' | translateWF: 'Client' }} *</label>
                <p-autoComplete
                  fluid
                  [formControl]="initialForm.controls.client"
                  #clientSearchRef="cslServerSearch"
                  [cslServerSearch]="fetchClients"
                  [suggestions]="clientSearchRef.results()"
                  (onSelect)="onClientSelected($event.value)"
                  field="name"
                  [forceSelection]="true"
                  placeholder="{{ 'SQD_Search_Client' | translateWF: 'Search client...' }}"
                  class="w-full"
                  [ngClass]="{ 'ng-invalid ng-dirty': initialForm.controls.client.invalid && initialForm.controls.client.touched }"
                >
                  <ng-template let-client pTemplate="item">
                    <div class="col">
                      <span class="font-semibold">{{ client.name }}</span>
                      @if (client.code) {
                        <small class="text-gray-500">{{ client.code }}</small>
                      }
                    </div>
                  </ng-template>
                </p-autoComplete>
              </div>
            </div>
          </div>
        } @else if (sqd()) {
          <div class="grid-12">
            <div class="span-6">
              <div class="form-box w-full">
                <label>{{ 'SQD_Client' | translateWF: 'Client' }}</label>
                <input pInputText [value]="sqd()!.clientName || ''" [disabled]="true" class="w-full" />
              </div>
            </div>
          </div>
        }
      </p-card>
    </p-panel>

    <!-- ===================== SHIPPING INFORMATION ===================== -->
    <p-panel [header]="'SQD_Shipping_Info' | translateWF: 'Shipping Information'" [toggleable]="true" [collapsed]="false">
      <p-card data-variant="flat">
        @if (isCreateMode()) {
          <div class="grid-12">
            <!-- Ship To Customer -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Shipping_To' | translateWF: 'Ship To' }}</label>
                <p-autoComplete
                  fluid
                  [formControl]="initialForm.controls.shippingTo"
                  #shippingToSearch1Ref="cslServerSearch"
                  [cslServerSearch]="fetchShippingTo"
                  [suggestions]="shippingToSearch1Ref.results()"
                  (onSelect)="onInitialShippingToSelected($event.value)"
                  field="name"
                  [forceSelection]="true"
                  placeholder="{{ 'SQD_Search_Business' | translateWF: 'Search business...' }}"
                  class="w-full"
                >
                  <ng-template let-client pTemplate="item">
                    <div class="col">
                      <span class="font-semibold">{{ client.name }}</span>
                      @if (client.code) {
                        <small class="text-gray-500">{{ client.code }}</small>
                      }
                    </div>
                  </ng-template>
                </p-autoComplete>
                <small class="text-gray-500">{{ 'SQD_Ship_To_Help' | translateWF: 'Leave empty to use client' }}</small>
              </div>
            </div>

            <!-- Ship To Address -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Ship_To_Address' | translateWF: 'Ship To Address' }} *</label>
                <p-select
                  [formControl]="initialForm.controls.shipToAddress"
                  [options]="shippingAddresses()"
                  optionLabel="address1"
                  placeholder="{{ 'SQD_Select_Address' | translateWF: 'Select address' }}"
                  class="w-full"
                  [ngClass]="{
                    'ng-invalid ng-dirty': initialForm.controls.shipToAddress.invalid && initialForm.controls.shipToAddress.touched,
                  }"
                >
                  <ng-template let-addr pTemplate="selectedItem">
                    <span *ngIf="addr">{{ addr.address1 }}, {{ addr.city }}</span>
                  </ng-template>
                  <ng-template let-addr pTemplate="item">
                    <div class="col">
                      <span>{{ addr.address1 }}</span>
                      <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                    </div>
                  </ng-template>
                </p-select>
              </div>
            </div>

            <!-- Shipment Type -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Shipment_Type' | translateWF: 'Shipment Type' }} *</label>
                <p-select
                  [formControl]="initialForm.controls.shipmentType"
                  [options]="getShipmentTypeOptions()"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="{{ 'SQD_Select_Shipment_Type' | translateWF: 'Select shipment type' }}"
                  class="w-full"
                />
              </div>
            </div>
          </div>
        } @else if (sqd()) {
          <form [formGroup]="shippingForm" class="grid-12">
            <!-- Ship To Customer -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Shipping_To' | translateWF: 'Ship To' }}</label>
                <p-autoComplete
                  fluid
                  formControlName="shippingTo"
                  #shippingToSearch2Ref="cslServerSearch"
                  [cslServerSearch]="fetchShippingTo"
                  [suggestions]="shippingToSearch2Ref.results()"
                  (onSelect)="onShippingToSelected($event.value)"
                  field="name"
                  [forceSelection]="true"
                  placeholder="{{ 'SQD_Search_Business' | translateWF: 'Search business...' }}"
                  class="w-full"
                >
                  <ng-template let-client pTemplate="item">
                    <div class="col">
                      <span class="font-semibold">{{ client.name }}</span>
                      @if (client.code) {
                        <small class="text-gray-500">{{ client.code }}</small>
                      }
                    </div>
                  </ng-template>
                </p-autoComplete>
                <small class="text-gray-500">{{ 'SQD_Ship_To_Help' | translateWF: 'Leave empty to use client' }}</small>
              </div>
            </div>

            <!-- Ship To Address -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Ship_To_Address' | translateWF: 'Ship To Address' }} *</label>
                <p-select
                  formControlName="shipToAddress"
                  [options]="shippingAddresses()"
                  optionLabel="address1"
                  placeholder="{{ 'SQD_Select_Address' | translateWF: 'Select address' }}"
                  class="w-full"
                >
                  <ng-template let-addr pTemplate="selectedItem">
                    <span *ngIf="addr">{{ addr.address1 }}, {{ addr.city }}</span>
                  </ng-template>
                  <ng-template let-addr pTemplate="item">
                    <div class="col">
                      <span>{{ addr.address1 }}</span>
                      <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                    </div>
                  </ng-template>
                </p-select>
              </div>
            </div>

            <!-- Shipment Type -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Shipment_Type' | translateWF: 'Shipment Type' }} *</label>
                <p-select
                  formControlName="shipmentType"
                  [options]="getShipmentTypeOptions()"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="{{ 'SQD_Select_Shipment_Type' | translateWF: 'Select shipment type' }}"
                  class="w-full"
                />
              </div>
            </div>

            <!-- Save Button -->
            @if (isEditable()) {
              <div class="span-12 row-end-stretch">
                <p-button [label]="'SQD_Save_Shipping' | translateWF: 'Save Shipping'" icon="pi pi-save" (click)="saveShippingInfo()" [disabled]="shippingForm.invalid" />
              </div>
            }
          </form>
        }
      </p-card>
    </p-panel>

    <!-- ===================== BILLING INFORMATION ===================== -->
    <p-panel [header]="'SQD_Billing_Info' | translateWF: 'Billing Information'" [toggleable]="true" [collapsed]="false">
      <p-card data-variant="flat">
        @if (isCreateMode()) {
          <div class="grid-12">
            <!-- Bill To Customer -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Bill_To' | translateWF: 'Bill To' }}</label>
                <p-autoComplete
                  fluid
                  [formControl]="initialForm.controls.billTo"
                  #billToSearch1Ref="cslServerSearch"
                  [cslServerSearch]="fetchBillTo"
                  [suggestions]="billToSearch1Ref.results()"
                  (onSelect)="onInitialBillToSelected($event.value)"
                  field="name"
                  [forceSelection]="true"
                  placeholder="{{ 'SQD_Search_Business' | translateWF: 'Search business...' }}"
                  class="w-full"
                >
                  <ng-template let-client pTemplate="item">
                    <div class="col">
                      <span class="font-semibold">{{ client.name }}</span>
                      @if (client.code) {
                        <small class="text-gray-500">{{ client.code }}</small>
                      }
                    </div>
                  </ng-template>
                </p-autoComplete>
                <small class="text-gray-500">{{ 'SQD_Bill_To_Help' | translateWF: 'Leave empty to use client' }}</small>
              </div>
            </div>

            <!-- Bill To Address -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Bill_To_Address' | translateWF: 'Bill To Address' }}</label>
                <p-select
                  [formControl]="initialForm.controls.billToAddress"
                  [options]="billingAddresses()"
                  optionLabel="address1"
                  placeholder="{{ 'SQD_Select_Address' | translateWF: 'Select address' }}"
                  class="w-full"
                >
                  <ng-template let-addr pTemplate="selectedItem">
                    <span *ngIf="addr">{{ addr.address1 }}, {{ addr.city }}</span>
                  </ng-template>
                  <ng-template let-addr pTemplate="item">
                    <div class="col">
                      <span>{{ addr.address1 }}</span>
                      <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                    </div>
                  </ng-template>
                </p-select>
              </div>
            </div>

            <!-- Payment Term -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Payment_Term' | translateWF: 'Payment Term' }} *</label>
                <csl-dropdown-master-data
                  [formControl]="initialForm.controls.paymentTermClientCode"
                  categoryCode="PaymentTermsClient"
                  [placeholder]="'SQD_Select_Payment_Term' | translateWF: 'Select payment term'"
                  styleClass="w-full"
                  [allowCreate]="false"
                />
              </div>
            </div>
          </div>
        } @else if (sqd()) {
          <div class="grid-12">
            <!-- Bill To Customer -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Bill_To' | translateWF: 'Bill To' }}</label>
                <p-autoComplete
                  fluid
                  [formControl]="billingForm.controls.billTo"
                  #billToSearch2Ref="cslServerSearch"
                  [cslServerSearch]="fetchBillTo"
                  [suggestions]="billToSearch2Ref.results()"
                  (onSelect)="onBillToSelected($event.value)"
                  field="name"
                  [forceSelection]="true"
                  placeholder="{{ 'SQD_Search_Business' | translateWF: 'Search business...' }}"
                  class="w-full"
                >
                  <ng-template let-client pTemplate="item">
                    <div class="col">
                      <span class="font-semibold">{{ client.name }}</span>
                      @if (client.code) {
                        <small class="text-gray-500">{{ client.code }}</small>
                      }
                    </div>
                  </ng-template>
                </p-autoComplete>
                <small class="text-gray-500">{{ 'SQD_Bill_To_Help' | translateWF: 'Leave empty to use default client' }}</small>
              </div>
            </div>

            <!-- Bill To Address -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Bill_To_Address' | translateWF: 'Bill To Address' }}</label>
                <p-select
                  [formControl]="billingForm.controls.billToAddress"
                  [options]="billingAddresses()"
                  optionLabel="address1"
                  placeholder="{{ 'SQD_Select_Address' | translateWF: 'Select billing address' }}"
                  class="w-full"
                >
                  <ng-template let-addr pTemplate="selectedItem">
                    <span *ngIf="addr">{{ addr.address1 }}, {{ addr.city }}</span>
                  </ng-template>
                  <ng-template let-addr pTemplate="item">
                    <div class="col">
                      <span>{{ addr.address1 }}</span>
                      <small class="text-gray-500">{{ addr.city }}, {{ addr.stateOrProvince }} {{ addr.zipCode }}</small>
                    </div>
                  </ng-template>
                </p-select>
              </div>
            </div>

            <!-- Payment Term -->
            <div class="span-6">
              <div class="form-box">
                <label>{{ 'SQD_Payment_Term' | translateWF: 'Payment Term' }} *</label>
                <csl-dropdown-master-data
                  [formControl]="billingForm.controls.paymentTermClientCode"
                  categoryCode="PaymentTermsClient"
                  [placeholder]="'SQD_Select_Payment_Term' | translateWF: 'Select payment term'"
                  styleClass="w-full"
                  [allowCreate]="false"
                />
              </div>
            </div>

            <!-- Invoice Comment -->
            <div class="span-12">
              <div class="form-box">
                <label>{{ 'SQD_Invoice_Comment' | translateWF: 'Invoice Comment' }}</label>
                <textarea pTextarea [formControl]="billingForm.controls.invoiceComment" [rows]="3" class="w-full"></textarea>
              </div>
            </div>

            <!-- Save Button -->
            @if (isEditable()) {
              <div class="span-12 row-end-stretch">
                <p-button [label]="'SQD_Save_Billing' | translateWF: 'Save Billing'" icon="pi pi-save" (click)="saveBillingInfo()" [disabled]="billingForm.invalid" />
              </div>
            }
          </div>
        }
      </p-card>
    </p-panel>

    <!-- ===================== SCANNED ROLLS (existing only) ===================== -->
    @if (sqd()) {
      <p-panel [header]="'SQD_Scanned_Rolls' | translateWF: 'Scanned Rolls'" [toggleable]="true" [collapsed]="false">
        <div class="col-xs">
          <!-- Scan Input (Draft only) -->
          @if (isDraft() && canEdit()) {
            <p-card>
              <div class="grid-auto-2">
                <div class="form-box">
                  <label>{{ 'SQD_Scan_Roll' | translateWF: 'Scan Roll' }}</label>
                  <div class="row-sm">
                    <input
                      pInputText
                      [ngModel]="scanInput()"
                      (ngModelChange)="scanInput.set($event)"
                      placeholder="{{ 'SQD_Enter_Roll_Number' | translateWF: 'Enter roll number...' }}"
                      class="flex-1"
                      (keydown.enter)="scanRoll()"
                    />
                    <p-button [label]="'SQD_Scan' | translateWF: 'Scan'" icon="pi pi-qrcode" (click)="scanRoll()" [disabled]="!scanInput()" />
                  </div>
                </div>
                <div class="form-box">
                  <label>{{ 'SQD_Scanned_Rolls_Item_Description' | translateWF: 'Scanned Rolls Item Description' }}</label>
                  <input pInputText [formControl]="rollPriceForm.controls.scannedRollsItemDescription" class="w-full" />
                </div>
                <div class="form-box">
                  <label>{{ 'SQD_Roll_Unit_Price' | translateWF: 'Roll Unit Price (per lb)' }}</label>
                  <div class="row-sm">
                    <p-inputNumber [formControl]="rollPriceForm.controls.scannedRollsUnitPrice" mode="currency" currency="USD" [minFractionDigits]="2" styleClass="w-full" />
                    <p-button [label]="'SQD_Save' | translateWF: 'Save'" icon="pi pi-save" (click)="saveRollPrice()" [disabled]="rollPriceForm.invalid" />
                  </div>
                </div>
              </div>
            </p-card>
          } @else if (sqd()!.scannedRollsUnitPrice || sqd()!.scannedRollsItemDescription) {
            <p-card>
              <div class="grid-auto-2">
                @if (sqd()!.scannedRollsItemDescription) {
                  <div>
                    <span class="text-gray-500 text-sm">{{
                      'SQD_Scanned_Rolls_Item_Description'
                        | translateWF
                          : 'Scanned Rolls
                Item Description'
                    }}</span>
                    <p class="font-semibold">{{ sqd()!.scannedRollsItemDescription }}</p>
                  </div>
                }
                @if (sqd()!.scannedRollsUnitPrice) {
                  <div>
                    <span class="text-gray-500 text-sm">{{ 'SQD_Roll_Unit_Price' | translateWF: 'Roll Unit Price (per lb)' }}</span>
                    <p class="font-semibold">{{ sqd()!.scannedRollsUnitPrice | currency: 'USD' }}</p>
                  </div>
                }
              </div>
            </p-card>
          }

          <!-- Rolls Table -->
          <p-card data-variant="compact">
            <p-table [value]="sqd()!.scannedRolls" [rowHover]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'SQD_Roll_Number' | translateWF: 'Roll Number' }}</th>
                  <th>{{ 'SQD_Item_Code' | translateWF: 'Item Code' }}</th>
                  <th>{{ 'SQD_Roll_Quality' | translateWF: 'Quality' }}</th>
                  <th>{{ 'SQD_Roll_Weight' | translateWF: 'Roll Final Weight (lbs)' }}</th>
                  @if (isDraft() && canEdit()) {
                    <th style="width: 80px"></th>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-roll>
                <tr>
                  <td class="font-semibold">{{ roll.number }}</td>
                  <td>{{ roll.itemCode || '-' }}</td>
                  <td>
                    <p-tag value="B-Grade" severity="warn" />
                  </td>
                  <td>{{ roll.finalWeight.weightInLbs | number: '1.2-2' }}</td>
                  @if (isDraft() && canEdit()) {
                    <td>
                      <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" (click)="removeRoll(roll.number)" />
                    </td>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="footer">
                <tr>
                  <td colspan="3" class="font-semibold text-right">{{ 'SQD_Total' | translateWF: 'Total' }}: {{ sqd()!.scannedRolls.length }} rolls</td>
                  <td class="font-semibold">{{ totalRollWeight() | number: '1.2-2' }} lbs</td>
                  @if (isDraft() && canEdit()) {
                    <td></td>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="isDraft() && canEdit() ? 5 : 4" class="text-center py-4">
                    {{ 'SQD_No_Rolls' | translateWF: 'No rolls scanned yet' }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </div>
      </p-panel>
      <p-panel [header]="'SQD_Scanned_Lot_Items' | translateWF: 'Lotes de Hilo'" [toggleable]="true" [collapsed]="false">
        <div class="col-xs">
          <!-- Scan Input (Draft only) -->
          @if (isDraft() && canEdit()) {
            <p-card>
              <div class="grid-auto-2">
                <div class="form-box">
                  <label>{{ 'SQD_Scan_Lot_Item' | translateWF: 'Escanear Lote' }}</label>
                  <div class="row-sm">
                    <input
                      pInputText
                      [ngModel]="lotItemScanInput()"
                      (ngModelChange)="lotItemScanInput.set($event)"
                      placeholder="{{ 'SQD_Enter_Lot_Number' | translateWF: 'Ingrese número de lote...' }}"
                      class="flex-1"
                      (keydown.enter)="scanLotItem()"
                    />
                    <p-button [label]="'SQD_Add' | translateWF: 'Agregar'" icon="pi pi-plus" (click)="scanLotItem()" [disabled]="!lotItemScanInput()" />
                  </div>
                </div>
                <div class="form-box">
                  <label>{{ 'SQD_Lot_Item_Description' | translateWF: 'Lot Item Description' }}</label>
                  <input pInputText [formControl]="lotItemPriceForm.controls.scannedLotItemsItemDescription" class="w-full" />
                  <small class="text-gray-500">{{
                    'SQD_Lot_Item_Description_Help'
                      | translateWF
                        : 'If left empty, the commercial invoice will use the lot
                item master description.'
                  }}</small>
                </div>
                <div class="form-box">
                  <label>{{ 'SQD_Lot_Item_Unit_Price' | translateWF: 'Precio Unitario Lote (por lb)' }}</label>
                  <div class="row-sm">
                    <p-inputNumber [formControl]="lotItemPriceForm.controls.scannedLotItemsUnitPrice" mode="currency" currency="USD" [minFractionDigits]="2" styleClass="w-full" />
                    <p-button [label]="'SQD_Save' | translateWF: 'Guardar'" icon="pi pi-save" (click)="saveLotItemPrice()" [disabled]="lotItemPriceForm.invalid" />
                  </div>
                </div>
              </div>
            </p-card>
          } @else if (sqd()!.scannedLotItemsUnitPrice || sqd()!.scannedLotItemsItemDescription) {
            <p-card>
              <div class="grid-auto-2">
                @if (sqd()!.scannedLotItemsItemDescription) {
                  <div>
                    <span class="text-gray-500 text-sm">{{ 'SQD_Lot_Item_Description' | translateWF: 'Lot Item Description' }}</span>
                    <p class="font-semibold">{{ sqd()!.scannedLotItemsItemDescription }}</p>
                  </div>
                }
                @if (sqd()!.scannedLotItemsUnitPrice) {
                  <div>
                    <span class="text-gray-500 text-sm">{{ 'SQD_Lot_Item_Unit_Price' | translateWF: 'Precio Unitario (por lb)' }}</span>
                    <p class="font-semibold">{{ sqd()!.scannedLotItemsUnitPrice | currency: 'USD' }}</p>
                  </div>
                }
              </div>
            </p-card>
          }

          <!-- Lot Items Table -->
          <p-card data-variant="compact">
            <p-table [value]="sqd()!.scannedLotItems" [rowHover]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'SQD_Lot_Number' | translateWF: 'No. Lote' }}</th>
                  <th>{{ 'SQD_Item_Code' | translateWF: 'Código Artículo' }}</th>
                  <th>{{ 'SQD_Item_Description' | translateWF: 'Descripción' }}</th>
                  <th>{{ 'SQD_Quantity' | translateWF: 'Cantidad (lb)' }}</th>
                  @if (isDraft() && canEdit()) {
                    <th style="width: 80px"></th>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-lotItem>
                @let strictItemLine = getStrictLotItem(lotItem);
                <tr>
                  <td class="font-semibold">{{ strictItemLine.number }}</td>
                  <td>{{ strictItemLine.itemCode || '-' }}</td>
                  <td>{{ strictItemLine.item?.itemDisplayName | localized }}</td>
                  <td>{{ strictItemLine.netQuantity | number: '1.2-2' }}</td>
                  @if (isDraft() && canEdit()) {
                    <td>
                      <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" (click)="removeLotItem(strictItemLine.number!)" />
                    </td>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="footer">
                <tr>
                  <td colspan="3" class="font-semibold text-right">{{ 'SQD_Total' | translateWF: 'Total' }}: {{ sqd()!.scannedLotItems.length }} lotes</td>
                  <td class="font-semibold">{{ totalLotItemWeight() | number: '1.2-2' }} lbs</td>
                  @if (isDraft() && canEdit()) {
                    <td></td>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="isDraft() && canEdit() ? 5 : 4" class="text-center py-4">
                    {{ 'SQD_No_Lot_Items' | translateWF: 'No hay lotes escaneados' }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </div>
      </p-panel>

      <!-- ===================== ITEM LINES (existing only) ===================== -->
      <p-panel [header]="'SQD_Item_Lines' | translateWF: 'Item Lines'" [toggleable]="true" [collapsed]="false">
        <ng-template pTemplate="icons">
          @if (isDraft() && canEdit()) {
            <p-button [label]="'SQD_Save_Items' | translateWF: 'Save Item Lines'" icon="pi pi-save" (click)="saveItemLines()" [disabled]="itemLinesForm.invalid" />
          }
        </ng-template>
        <p-panel [header]="'SQD_Search_Item' | translateWF: 'Search Item'" data-tone="surface">
          <ng-template pTemplate="icons">
            @if (isDraft() && canEdit()) {
              <p-autocomplete
                fluid
                #itemSearchRef="cslServerSearch"
                [cslServerSearch]="fetchItems"
                [suggestions]="itemSearchRef.results()"
                (onSelect)="onItemSelectForLine($event.value)"
                [(ngModel)]="selectedItemForLine"
                [ngModelOptions]="{ standalone: true }"
                optionLabel="itemCode"
                [forceSelection]="true"
                [showClear]="true"
              >
                <ng-template let-item #item>
                  <div>
                    <span class="font-semibold">{{ item.itemCode }}</span>
                    <span class="ml-2 text-surface-500">{{ getItemDisplayLabel(item) }}</span>
                  </div>
                </ng-template>
              </p-autocomplete>
            }
          </ng-template>
          <p-card data-variant="compact">
            <p-table [value]="itemLinesForm.controls" [rowHover]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'SQD_Item' | translateWF: 'Item' }}</th>
                  <th>{{ 'SQD_Quantity' | translateWF: 'Quantity' }}</th>
                  <th style="width: 110px">{{ 'SQD_UOM' | translateWF: 'UOM' }}</th>
                  <th>{{ 'SQD_Bulks' | translateWF: 'Bulks' }}</th>
                  <th>{{ 'SQD_Unit_Price' | translateWF: 'Unit Price' }}</th>
                  <th>{{ 'SQD_Total' | translateWF: 'Total' }}</th>
                  @if (isDraft() && canEdit()) {
                    <th style="width: 80px"></th>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-lineGroup let-i="rowIndex">
                <tr>
                  @if (isDraft() && canEdit()) {
                    <td>
                      <strong>({{ lineGroup.getRawValue().itemCode }})</strong>
                      <span class="ml-1">{{ lineGroup.getRawValue().itemDescription || '' }}</span>
                    </td>
                    <td>
                      <p-inputNumber [formControl]="lineGroup.controls.quantity" [minFractionDigits]="2" styleClass="w-full" />
                    </td>
                    <td>
                      {{ uomOptions[lineGroup.controls.uom.value].label || '-' }}
                    </td>
                    <td>
                      <p-inputNumber [formControl]="lineGroup.controls.packageCount" [min]="0" styleClass="w-full" />
                    </td>
                    <td>
                      <p-inputNumber [formControl]="lineGroup.controls.unitPrice" mode="currency" currency="USD" [minFractionDigits]="2" styleClass="w-full" />
                    </td>
                    <td class="font-semibold">
                      {{ (lineGroup.controls.quantity.value ?? 0) * (lineGroup.controls.unitPrice.value ?? 0) | currency: 'USD' }}
                    </td>
                    <td>
                      <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" (click)="removeItemLine(i)" />
                    </td>
                  } @else {
                    <td>
                      <strong>({{ lineGroup.getRawValue().itemCode }})</strong>
                      <span class="ml-1">{{ lineGroup.getRawValue().itemDescription || '' }}</span>
                    </td>
                    <td>{{ lineGroup.getRawValue().quantity | number: '1.2-2' }}</td>
                    <td>{{ uomOptions[lineGroup.getRawValue().uom].label || '-' }}</td>
                    <td>{{ lineGroup.getRawValue().packageCount }}</td>
                    <td>{{ lineGroup.getRawValue().unitPrice | currency: 'USD' }}</td>
                    <td class="font-semibold">
                      {{ (lineGroup.getRawValue().quantity ?? 0) * (lineGroup.getRawValue().unitPrice ?? 0) | currency: 'USD' }}
                    </td>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="isDraft() && canEdit() ? 7 : 6" class="text-center py-4">
                    {{ 'SQD_No_Items' | translateWF: 'No item lines added' }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </p-panel>
      </p-panel>
    }
  </div>
</main>

<p-confirmDialog />
